import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Stack } from "@mui/material";

import { getAuthUser, getUserLocation } from "../../redux";
import { DocuBuilder, HubHookFormWrapper, PageContent } from "../../components";
import {
  useGetQuoteByNumber,
  usePostQuote,
  usePutQuote,
} from "../../hooks/react-query/useSalesData";
import { QUOTE_TEMPLATES } from "../../constants/templates";

import QuoteBuilderForm from "./QuoteBuilderForm";
import QuoteBuilderNav from "./QuoteBuilderNav";
import SendCostingSheetButton from "./components/SendCostingSheetButton";
import PartFieldsSync from "./components/PartFieldsSync";
import { useCostingSheetUpload } from "./components/useCostingSheetUpload";

import { getDefaultValues, getUpdatePayload, tabs } from "./helper";

const mapStateToProps = (state) => ({
  user: getAuthUser(state),
  locationId: getUserLocation(state),
});

export const QuoteBuilderPage = connect(mapStateToProps)(({
  user = {},
  locationId = 1,
}) => {
  const [tab, setTab] = useState(1);
  const { id: quoteNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, isLoading } = useGetQuoteByNumber(quoteNumber);
  const { upload: uploadCostingSheet } = useCostingSheetUpload({
    quoteId: data?.id,
  });

  // Honor `?upload=1` arriving from the kanban stage prompt — wait for the
  // costing sheet to be in the DOM, fire upload once, and strip the flag.
  const autoUploadFiredRef = useRef(false);
  useEffect(() => {
    if (autoUploadFiredRef.current) return;
    if (searchParams.get("upload") !== "1") return;
    if (!data?.id) return;
    if (!document.getElementById("page-1")) return;
    autoUploadFiredRef.current = true;
    uploadCostingSheet();
    const next = new URLSearchParams(searchParams);
    next.delete("upload");
    setSearchParams(next, { replace: true });
  }, [searchParams, data?.id, uploadCostingSheet, setSearchParams]);
  const {
    mutate: create,
    isSuccess: isCreateSuccess,
    data: newQuote,
  } = usePostQuote();
  const { mutate: update, isSuccess: isUpdateSuccess } = usePutQuote();

  const isExisting = !!data?.id;

  useEffect(() => {
    if (isCreateSuccess && newQuote?.quoteNumber) {
      navigate(`/quotes/${newQuote.quoteNumber}`);
    }
  }, [isCreateSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) window.location.reload();
  }, [isUpdateSuccess]);

  const onSubmit = (formData) => {
    const payload = getUpdatePayload(formData);
    if (isExisting) update({ id: data.id, ...payload });
    else create({ ...payload, locationId });
  };

  const defaultValues = getDefaultValues(data, user, locationId);
  const Template = QUOTE_TEMPLATES[0]?.Template;
  const tabValues = tabs?.find((t) => t.id === tab);

  return (
    <PageContent disablePadding isLoading={!!quoteNumber && isLoading}>
      <Stack direction="row">
        <QuoteBuilderNav value={tab} setValue={setTab} />
        <HubHookFormWrapper
          onSubmit={onSubmit}
          sx={{ height: "100%" }}
          defaultValues={defaultValues}
        >
          <PartFieldsSync />
          <DocuBuilder
            id={data?.id}
            name={data?.quoteNumber}
            title={data?.quoteNumber || "New Quote"}
            data={data || {}}
            label="Quote"
            includeName={false}
            disabled={false}
            isLoading={isLoading}
            isPreview={!quoteNumber}
            Template={({ ...rest }) => (
              <Template
                {...rest}
                showCosting={tabValues?.showCosting}
                showQuote={tabValues?.showQuote}
              />
            )}
            sideBarWidth="450px"
            FormComponent={
              <QuoteBuilderForm quoteId={data?.id} currentUserId={user?.id} />
            }
            extraActions={
              <SendCostingSheetButton quoteId={data?.id} />
            }
          />
        </HubHookFormWrapper>
      </Stack>
    </PageContent>
  );
});

export default QuoteBuilderPage;
